import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Text from 'ol/style/Text'
import Circle from 'ol/style/Circle'
import MultiPoint from 'ol/geom/MultiPoint'
import { LineString, MultiPolygon, Point, Polygon } from 'ol/geom'
import { isArray } from 'lodash'

/**
 * Sets z-index for style or styles
 * @param {Style|Array<Style>} style style or styles to set Z-index for
 * @param {Number} zIndex z-index to set
 * @returns the style with z-index set
 */
export const withZIndex = (style: Style | Style[], zIndex: number) => {
  if (isArray(style)) {
    style.forEach(x => x.setZIndex(zIndex))
  } else {
    style.setZIndex(zIndex)
  }
  return style
}

type ColorName =
  | 'blue'
  | 'cyan'
  | 'orange'
  | 'purple'
  | 'white'
  | 'black'
  | 'lightGray'
  | 'gray'
  | 'red'
  | 'transparent'
  | 'green'
  | 'pink'
  | 'greenDot'
  | 'orangeDot'
  | 'grayDot'

type ColorArray = [number, number, number] | [number, number, number, number]

export const colors: Record<ColorName, ColorArray> = {
  blue: [43, 140, 190],
  cyan: [0, 166, 204, 0.8],
  orange: [220, 120, 33],
  purple: [220, 33, 120],
  white: [255, 255, 255],
  black: [68, 68, 69],
  lightGray: [245, 245, 245],
  gray: [151, 151, 151],
  red: [226, 84, 83],
  transparent: [0, 0, 0, 0],
  green: [43, 200, 135],
  pink: [255, 66, 83],
  greenDot: [70, 193, 60, 0.6],
  orangeDot: [255, 193, 79],
  grayDot: [255, 255, 255, 0.6],
}

export const userPalette = [
  '#e9aa36',
  '#e95d36',
  '#36b6e9',
  '#8d36e9',
  '#e93678',
]

export const selectedFill = new Fill({ color: [...colors.white, 0.05] })
export const selectedStroke = new Stroke({ color: colors.purple, width: 4 })

export const unselectedFill = new Fill({ color: [...colors.white, 0] })
export const unselectedStroke = new Stroke({ color: colors.blue, width: 2 })

export const nonInteractiveStroke = new Stroke({
  color: [...colors.black, 0.8],
  width: 1,
})

export const maxOrthoZoom = 30
export const maxImageZoom = 6

type StyleOptions = {
  vertexHandles?: boolean
  color?: ColorArray
  fill?: Fill
}

export const createSelectedInteractivePolygonStyle = (
  options: StyleOptions = {}
) => {
  const enabledVertexHandles =
    options.vertexHandles || options.vertexHandles === undefined
  const color = options.color || colors.purple
  return [
    new Style({
      fill: options.fill || selectedFill,
      stroke: new Stroke({ color: color, width: 4 }),
    }),
    new Style({
      image: new Circle({
        radius: 10,
        fill: new Fill({ color: colors.white }),
      }),
      geometry: enabledVertexHandles
        ? function (f) {
            const geometry = f.getGeometry()
            if (!geometry) {
              return
            }
            const vertexCoords =
              geometry instanceof Point
                ? [geometry.getCoordinates()]
                : geometry instanceof LineString
                ? geometry.getCoordinates()
                : geometry instanceof Polygon
                ? geometry.getCoordinates().flat(1)
                : geometry instanceof MultiPolygon
                ? geometry.getCoordinates()[0][0]
                : []
            return new MultiPoint(vertexCoords)
          }
        : undefined,
    }),
  ]
}

export const createInteractivePolygonStyle = () =>
  new Style({
    fill: unselectedFill,
    stroke: unselectedStroke,
  })

export const createNonInteractivePolygonStyle = () =>
  new Style({
    stroke: nonInteractiveStroke,
  })

export const font =
  "normal 10px 'Roboto','Helvetica Neue','Helvetica','Arial',sans-serif"

export const createLabelText = () =>
  new Text({
    font,
    textAlign: 'left',
    padding: [5, 5, 5, 5],
    overflow: true,
    fill: new Fill({ color: colors.white }),
    backgroundFill: new Fill({ color: [...colors.blue, 0.8] }),
  })

export const zIndexes = {
  ortho: 1,
  dem: 20,
  plantHealth: 30,
  prescription: 40,
  plantCounts: 50,
  fieldBoundary: 1000,
  measure: 1100,
  coordinates: 1200,
  sourceImagery: 1300,
  flightPlans: 1400,
  zonalStatistics: 1500,
  plantDetection: 1600,
  plantCountAreas: 1700,
  customLayers: 1800,
  annotations: 1800,
  vectorEditing: 20000,
}

export const boundsStyle = new Style({
  fill: new Fill({ color: 'rgba(255, 255, 255, 0.2)' }),
  stroke: new Stroke({ color: '#d8d8d8', width: 2 }),
})

export const createMiniMapUnSelectedFeatureStyle = (
  options: { color?: ColorArray; strokeWidth?: number } = {}
) => {
  const color = options.color || colors.blue
  const strokeWidth = options.strokeWidth || 0.5
  return new Style({
    image: new Circle({
      radius: 6,
      fill: new Fill({ color: color || colors.blue }),
      stroke: new Stroke({ color: color, width: strokeWidth }),
    }),
  })
}

export const createMiniMapSelectedFeatureStyle = (
  options: {
    color?: ColorArray
    radius?: number
    strokeWidth?: number
    strokeColor?: ColorArray
  } = {}
) => {
  const radius = options.radius || 6
  const color = options.color || colors.red
  const strokeColor = options.strokeColor || color
  const strokeWidth = options.strokeWidth || 2
  return new Style({
    image: new Circle({
      radius,
      fill: new Fill({ color: color }),
      stroke: new Stroke({
        color: strokeColor,
        width: strokeWidth,
      }),
    }),
  })
}

/**
 * Colors that are suitable for vector overlays, for example used when
 * reviewing PlantAI detections
 */
export const overlayColors = ['#e95d36', '#36b6e9']

/**
 * Another set of colors for vector overlays, that should contrast enough
 * compared to `overlayColors` so both could be used at the same time.
 * (For example used for displaying PlantAI detections' rows).
 */
export const altOverlayColors = ['#e93678', '#8d36e9']
